import { useMemo } from 'react';
import SectionLabel from '../common/SectionLabel';
import { formatCurrency, formatPercent } from '../../utils/formatting';
import { ACTUALS_CUTOFF_2026 } from '../../context/FinancialDataContext';

const MONTH_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

function sumCat(monthData, category) {
  if (!monthData?.[category]) return 0;
  return Object.values(monthData[category]).reduce((s, v) => s + (v || 0), 0);
}

export default function RevenuePace({ global2026Monthly }) {
  const pace = useMemo(() => {
    if (!global2026Monthly?.months) return null;

    // YTD actuals (closed months only)
    let ytdActualRevenue = 0;
    let ytdActualCogs = 0;
    let ytdActualExpenses = 0;
    for (let i = 0; i < ACTUALS_CUTOFF_2026; i++) {
      const md = global2026Monthly.months[MONTH_KEYS[i]];
      ytdActualRevenue += sumCat(md, 'income');
      ytdActualCogs += sumCat(md, 'cogs');
      ytdActualExpenses += sumCat(md, 'expenses');
    }
    const ytdActualNOI = ytdActualRevenue - ytdActualCogs - ytdActualExpenses;

    // Full year projection (actuals + estimates). Fold in the won-deals
    // overlay so the projection matches the Dashboard's "2026 Projected
    // Revenue" card. The overlay is 0 for actual months, so YTD/annualized
    // (computed from actuals above) stay pure — only the full-year
    // projection and its NOI pick up the closed-won revenue.
    let fullYearRevenue = 0;
    let fullYearCogs = 0;
    let fullYearExpenses = 0;
    for (let i = 0; i < MONTH_KEYS.length; i++) {
      const md = global2026Monthly.months[MONTH_KEYS[i]];
      fullYearRevenue += sumCat(md, 'income');
      fullYearCogs += sumCat(md, 'cogs');
      fullYearExpenses += sumCat(md, 'expenses');
    }
    const fullYearNOI = fullYearRevenue - fullYearCogs - fullYearExpenses;

    // Annualized pace based on actuals only
    // "If every month performed like Jan-Mar, you'd end at..."
    const annualizedRevenue = Math.round(ytdActualRevenue * (12 / ACTUALS_CUTOFF_2026));
    const annualizedNOI = Math.round(ytdActualNOI * (12 / ACTUALS_CUTOFF_2026));

    // Pace vs projection
    const revenuePaceGap = annualizedRevenue - fullYearRevenue;
    const revenuePacePct = fullYearRevenue > 0 ? revenuePaceGap / fullYearRevenue : 0;

    // How far through the year's revenue are we?
    const ytdPctOfProjection = fullYearRevenue > 0 ? ytdActualRevenue / fullYearRevenue : 0;
    // Expected % through year based on months elapsed
    const expectedPct = ACTUALS_CUTOFF_2026 / 12;

    return {
      ytdActualRevenue,
      ytdActualNOI,
      annualizedRevenue,
      annualizedNOI,
      fullYearRevenue,
      fullYearNOI,
      revenuePaceGap,
      revenuePacePct,
      ytdPctOfProjection,
      expectedPct,
      monthsClosed: ACTUALS_CUTOFF_2026,
    };
  }, [global2026Monthly]);

  if (!pace) return null;

  const isRunningHot = pace.revenuePacePct >= 0;
  const paceLabel = isRunningHot ? 'Running Hot' : Math.abs(pace.revenuePacePct) < 0.05 ? 'On Pace' : 'Running Cold';
  const paceColor = isRunningHot ? 'text-deep-teal' : Math.abs(pace.revenuePacePct) < 0.05 ? 'text-navy' : 'text-rose';
  const paceBg = isRunningHot ? 'bg-deep-teal/10' : Math.abs(pace.revenuePacePct) < 0.05 ? 'bg-navy/10' : 'bg-rose/10';

  return (
    <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
      <div className="flex items-center justify-between">
        <SectionLabel>Revenue Pace</SectionLabel>
        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-[3px] ${paceBg} ${paceColor}`}>
          {paceLabel}
        </span>
      </div>

      {/* Annualized pace - the headline number */}
      <div className="mt-2 mb-3">
        <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#999] mb-0.5">
          Annualized from Q1 Actuals
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-[28px] font-bold text-navy">{formatCurrency(pace.annualizedRevenue)}</span>
          <span className={`text-[12px] font-bold ${paceColor}`}>
            {pace.revenuePacePct >= 0 ? '+' : ''}{formatPercent(pace.revenuePacePct)} vs projection
          </span>
        </div>
      </div>

      {/* Progress bar: YTD actual as % of full year */}
      <div className="mb-4">
        <div className="flex justify-between text-[10px] text-[#999] mb-1">
          <span>YTD: {formatCurrency(pace.ytdActualRevenue)}</span>
          <span>Projection: {formatCurrency(pace.fullYearRevenue)}</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-2 relative">
          <div
            className="h-2 rounded-full bg-deep-teal transition-all"
            style={{ width: `${Math.min(pace.ytdPctOfProjection * 100, 100)}%` }}
          />
          {/* Expected pace marker */}
          <div
            className="absolute top-0 h-2 w-0.5 bg-navy/40"
            style={{ left: `${pace.expectedPct * 100}%` }}
            title={`Expected: ${(pace.expectedPct * 100).toFixed(0)}% through year`}
          />
        </div>
        <div className="flex justify-between text-[9px] text-[#aaa] mt-0.5">
          <span>{(pace.ytdPctOfProjection * 100).toFixed(1)}% collected</span>
          <span>{pace.monthsClosed} of 12 months closed</span>
        </div>
      </div>

      {/* NOI pace */}
      <div className="pt-3 border-t border-gray-100">
        <div className="flex justify-between items-center text-[12px]">
          <span className="text-[#888]">NOI Pace (annualized)</span>
          <span className={`font-bold ${pace.annualizedNOI >= 0 ? 'text-deep-teal' : 'text-rose'}`}>
            {formatCurrency(pace.annualizedNOI)}
          </span>
        </div>
        <div className="flex justify-between items-center text-[12px] mt-1">
          <span className="text-[#888]">NOI Projection</span>
          <span className={`font-bold ${pace.fullYearNOI >= 0 ? 'text-deep-teal' : 'text-rose'}`}>
            {formatCurrency(pace.fullYearNOI)}
          </span>
        </div>
      </div>

      {/* Info box */}
      <div className="mt-3 pt-3 border-t border-gray-100 text-[10px] text-[#999] leading-relaxed">
        Annualized revenue takes your {pace.monthsClosed}-month actuals and extrapolates to 12 months.
        "Running Hot" means actuals are ahead of the full-year projection.
        The progress bar shows YTD collected vs projection, with the dark marker at the expected pace ({(pace.expectedPct * 100).toFixed(0)}%).
      </div>
    </div>
  );
}
